import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import CustomShaderMaterial from "three-custom-shader-material";

import vertexShader from '../../shaders/ocean_plane_vertex.glsl?raw';
import fragmentShader from '../../shaders/ocean_plane_fragment.glsl?raw';

const COLOR_BASE_NEAR = new THREE.Color(0x2C7ECF);
const COLOR_BASE_FAR = new THREE.Color(0x031566);
const WAVE_SPEED = 1.2;
// Загальна висота хвиль по всій поверхні океану
const WAVE_HEIGHT = 0.3;
// Висота хвиль безпосередньо біля берега (ефект прибою)
const SHORE_WAVE_HEIGHT = 0.15;
const TEXTURE_SIZE = 20;
const OCEAN_EXTEND = 100;

interface OceanProps {
  groundSize: [number, number, number];
  groundPosition: [number, number, number];
}

export default function Ocean({ groundSize, groundPosition }: OceanProps) {
  const materialRef = useRef<THREE.ShaderMaterial>(null);

  const [groundW, , groundD] = groundSize;
  const [gx, gy, gz] = groundPosition;

  const oceanWidth = groundW + OCEAN_EXTEND * 2;
  const oceanDepth = groundD + OCEAN_EXTEND * 2;

  const uniforms = useMemo(() => ({
    uTime: { value: 0 },
    uColorFar: { value: COLOR_BASE_FAR },
    uWaveSpeed: { value: WAVE_SPEED },
    uWaveAmplitude: { value: WAVE_HEIGHT },
    uShoreWaveHeight: { value: SHORE_WAVE_HEIGHT },
    // співвідношення розміру ґрунту до розміру всього плейна — для маски берегових хвиль
    uGroundRatio: { value: new THREE.Vector2(groundW / oceanWidth, groundD / oceanDepth) },
    uTextureSize: { value: TEXTURE_SIZE },
    uWaveColor: { value: new THREE.Color(1, 1, 1) },
    uWaveOpacity: { value: 0.7 }
  }), []);

  useFrame(({ clock }) => {
    if (materialRef.current?.uniforms) {
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
    }
  });

  const groundTopY = gy + groundSize[1] / 2.5;
  const oceanY = groundTopY - 0.05;

  return (
    <mesh
      position={[gx, oceanY, gz]}
      rotation={[-Math.PI / 2, 0, 0]}
    >
      <planeGeometry args={[oceanWidth, oceanDepth, 64, 64]} />
      <CustomShaderMaterial
        ref={materialRef as any}
        baseMaterial={THREE.MeshStandardMaterial}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        color={COLOR_BASE_NEAR}
        transparent
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}
